import React, { useState } from "react";
import PublicNavbar from "../../components/public/PublicNavbar";
import InquiryForm from "../../components/public/InquiryForm";
import { FaIndustry, FaStore, FaBuilding, FaGlassCheers, FaCompass, FaWhatsapp, FaShieldAlt } from "react-icons/fa";
import { buildWhatsAppUrl } from "../../utils/whatsapp";

const applicationsData = [
    {
        title: "Industrial Floor & Safety Projection",
        category: "Industrial & Manufacturing",
        icon: <FaIndustry />,
        image: "/assets/lumigo/projector-4.jpg",
        description: "Virtual pedestrian crossings, forklift hazard warnings, stop signs, and safety boundaries projected directly onto industrial warehouse and factory floors.",
        benefits: [
            "Zero paint wear, peeling, or costly floor re-striping",
            "High-contrast visibility in bright ambient warehouse lighting",
            "IP65 sealed aluminium casing protects against airborne dust and vibration",
            "Dynamic pulsing or flashing options for hazardous machinery zones"
        ]
    },
    {
        title: "Factory & Corporate Warehouse Branding",
        category: "Enterprise Infrastructure",
        icon: <FaShieldAlt />,
        image: "/assets/lumigo/projector-8.jpg",
        description: "Promote manufacturing excellence, corporate slogans, safety milestones, and company branding across spacious factory entrances and distribution centres.",
        benefits: [
            "Project expansive 5m to 20m logos onto high industrial ceilings or walls",
            "Energy-efficient OSRAM LED engine runs continuously with low power draw",
            "Rapid gobo disc replacement when branding or messages update"
        ]
    },
    {
        title: "Hotel, Restaurant & Retail Facade Branding",
        category: "Commercial & Hospitality",
        icon: <FaStore />,
        image: "/assets/lumigo/projector-6.jpg",
        description: "Turn sidewalks, entrance pavements, and architectural building facades into radiant brand touchpoints that draw evening foot traffic.",
        benefits: [
            "Captures pedestrian and vehicular attention from up to 25+ metres away",
            "Eliminates expensive outdoor physical sign permits and structural drilling",
            "Multiple logo rotation options to showcase brand sponsors or menus"
        ]
    },
    {
        title: "Architectural Exterior Illuminations",
        category: "Architecture & Landmarks",
        icon: <FaBuilding />,
        image: "/assets/lumigo/projector-10.jpg",
        description: "Illuminate multi-story commercial towers, heritage monuments, and event venues with ultra-crisp custom pattern projections.",
        benefits: [
            "Long-throw optical focal lenses calibrated for vertical facades",
            "High optical efficiency ensures uniform light distribution without edge blur",
            "Weatherproof IP65 outdoor endurance in extreme monsoon and heat"
        ]
    },
    {
        title: "Weddings, Galas & Luxury Events",
        category: "Events & Entertainment",
        icon: <FaGlassCheers />,
        image: "/assets/lumigo/projector-12.jpg",
        description: "Custom monogram projection, newlywed initials, dance floor textures, and corporate gala sponsor displays.",
        benefits: [
            "Ultra-sharp laser-etched dichroic glass gobos capture delicate calligraphy",
            "Plug-and-play installation with adjustable beam angles",
            "Available in both static single gobo and rotating configurations"
        ]
    },
    {
        title: "Festive, Cultural & Decorative Rangoli Projections",
        category: "Cultural & Festive",
        icon: <FaCompass />,
        image: "/assets/lumigo/projector-14.jpg",
        description: "Vibrant traditional rangoli patterns, Diwali illuminations, and festive motifs projected cleanly onto temple plazas, courtyards, and community centres.",
        benefits: [
            "High-chroma multi-colour glass gobos reproduce intricate Indian art",
            "Continuous 360-degree rotation mimics dynamic kaleidoscope effects",
            "Zero mess, no powder cleaning, and reusable year after year"
        ]
    }
];

const Applications = () => {
    const [showInquiry, setShowInquiry] = useState(false);

    return (
        <div className="min-h-screen bg-[#FFFFFF] text-[#263746] font-sans">
            <PublicNavbar />

            {/* Header Banner */}
            <div className="bg-[#F7F5EF] border-b border-[#E5E7EB] py-14 px-6 text-center">
                <div className="max-w-4xl mx-auto">
                    <span className="text-[#E3B629] text-xs font-black tracking-[0.25em] uppercase block mb-2">
                        Turnkey Commercial Solutions
                    </span>
                    <h1 className="text-3xl sm:text-5xl font-extrabold text-[#263746] leading-tight">
                        Commercial Gobo Projection Applications
                    </h1>
                    <p className="text-[#687482] text-sm sm:text-base mt-4 max-w-2xl mx-auto leading-relaxed">
                        LUMIGO provides precision optical projection systems engineered for intense clarity, weather resistance, and heavy commercial service.
                    </p>
                </div>
            </div>

            {/* Application Cards */}
            <div className="max-w-7xl mx-auto px-6 py-16 space-y-16">
                {applicationsData.map((app, idx) => (
                    <div
                        key={idx}
                        className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${idx % 2 === 1 ? "lg:flex-row-reverse" : ""}`}
                    >
                        <div className={`lg:col-span-6 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-md group bg-[#F7F5EF]">
                                <img
                                    src={app.image}
                                    alt={app.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                                <span className="absolute bottom-4 left-4 bg-[#E3B629] text-[#263746] text-xs font-black px-3 py-1 rounded uppercase tracking-wider shadow">
                                    {app.category}
                                </span>
                            </div>
                        </div>

                        <div className={`lg:col-span-6 space-y-5 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                            <div className="flex items-center gap-3 text-[#E3B629] text-2xl">
                                {app.icon}
                                <span className="text-xs uppercase tracking-widest font-bold text-[#687482]">
                                    Specialized Deployment
                                </span>
                            </div>

                            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#263746] leading-snug">
                                {app.title}
                            </h2>

                            <p className="text-[#687482] text-sm leading-relaxed">
                                {app.description}
                            </p>

                            <div className="space-y-2 pt-2">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-[#263746]">Key Advantages:</h4>
                                <ul className="space-y-2 text-xs text-[#687482]">
                                    {app.benefits.map((b, bIdx) => (
                                        <li key={bIdx} className="flex items-start gap-2">
                                            <span className="text-[#E3B629] font-bold mt-0.5">•</span>
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="pt-4 flex flex-wrap gap-4">
                                <button
                                    onClick={() => setShowInquiry(true)}
                                    className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-6 py-2.5 rounded-lg text-xs uppercase tracking-wider transition shadow-sm"
                                >
                                    Enquire for this Application
                                </button>
                                <a
                                    href={buildWhatsAppUrl(`Hello LUMIGO,\n\nI am interested in your ${app.title} solutions. Please share hardware options and quotation details.`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-[#168B53] hover:bg-[#127244] text-white font-bold px-5 py-2.5 rounded-lg text-xs transition flex items-center gap-1.5 shadow-sm"
                                >
                                    <FaWhatsapp /> WhatsApp Enquiry
                                </a>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Bottom Callout */}
            <div className="bg-[#F7F5EF] border-t border-[#E5E7EB] py-16 px-6 text-center">
                <div className="max-w-3xl mx-auto">
                    <h3 className="text-2xl sm:text-3xl font-extrabold text-[#263746] mb-4">
                        Need Custom Engineering & Lux Calculations?
                    </h3>
                    <p className="text-[#687482] text-xs sm:text-sm mb-6 leading-relaxed">
                        Every architectural surface and ambient light environment is unique. Contact our technical team with your installation height and throw distance for an exact lens recommendation.
                    </p>
                    <button
                        onClick={() => setShowInquiry(true)}
                        className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-8 py-3 rounded-lg text-xs uppercase tracking-wider transition shadow-sm"
                    >
                        Request Technical Quotation
                    </button>
                </div>
            </div>

            {showInquiry && <InquiryForm onClose={() => setShowInquiry(false)} />}
        </div>
    );
};

export default Applications;
